import React, { useRef, useState, useEffect } from "react"
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Alert,
  alpha,
  useTheme,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { useAuth0 } from "@auth0/auth0-react"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import useMenuParser from "hooks/useMenuParser"
import { savePendingMenu } from "helpers/pendingMenu"
import { cropImageByBox } from "utils/cropImage"

// Блок на стартовой странице: загрузить или сфотографировать текущее меню ресторана,
// распознать его на отдельные блюда и создать учётную запись с этими блюдами.
const UploadMenuSection = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { loginWithRedirect } = useAuth0()
  const { loading, error, parseMenu } = useMenuParser()

  const uploadInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const [previewUrl, setPreviewUrl] = useState("")
  const [dishes, setDishes] = useState(null)
  const [hasCamera, setHasCamera] = useState(false)

  // Кнопку «Сфотографировать» показываем только на устройствах с камерой (телефоны, планшеты).
  useEffect(() => {
    let cancelled = false
    if (!navigator.mediaDevices?.enumerateDevices) return
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (!cancelled) setHasCamera(devices.some((d) => d.kind === "videoinput"))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const handleFile = async (file) => {
    if (!file) return

    setDishes(null)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl)

    const result = await parseMenu(file)
    if (!result) return

    // Для блюд, у которых на фото есть собственная картинка (box), вырезаем фрагмент
    // через canvas и кладём его в image (data-URI). Cloudinary зальёт его при импорте.
    const withImages = await Promise.all(
      result.map(async (dish) => {
        if (dish.box) {
          const cropped = await cropImageByBox(objectUrl, dish.box)
          if (cropped) return { ...dish, image: cropped }
        }
        return { ...dish, image: "" }
      })
    )

    setDishes(withImages)
  }

  const onUploadChange = (e) => handleFile(e.target.files?.[0])
  const onCameraChange = (e) => handleFile(e.target.files?.[0])

  // Сохраняем распознанные блюда и отправляем на регистрацию.
  // После логина блюда импортируются в новый restaurant (см. userContext).
  const handleCreateAccount = () => {
    savePendingMenu(dishes)
    loginWithRedirect({ screen_hint: "signup" })
  }

  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "background.paper",
        py: { xs: 6, md: 8 },
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      }}
    >
      <Box sx={{ maxWidth: 720, mx: "auto", px: 2, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.75,
            mb: 2,
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            borderRadius: "100px",
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "secondary.main",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18 }} />
          {t("Powered by AI")}
        </Box>

        <Typography variant="h3" sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" }, mb: 1.5 }}>
          {t("Upload a photo of your current menu")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t("Menu upload subtitle")}
        </Typography>

        {/* Скрытые input'ы: загрузка и съёмка */}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onUploadChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onCameraChange}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<UploadFileIcon />}
            disabled={loading}
            onClick={() => uploadInputRef.current?.click()}
            sx={{ px: 4, py: 1.5, fontSize: "1.05rem", borderRadius: "12px" }}
          >
            {t("Upload photo")}
          </Button>
          {hasCamera && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<PhotoCameraIcon />}
              disabled={loading}
              onClick={() => cameraInputRef.current?.click()}
              sx={{ px: 4, py: 1.5, fontSize: "1.05rem", borderRadius: "12px", borderWidth: 2, "&:hover": { borderWidth: 2 } }}
            >
              {t("Take a photo")}
            </Button>
          )}
        </Stack>

        {previewUrl && (
          <Box sx={{ mb: 3 }}>
            <Box
              component="img"
              src={previewUrl}
              alt={t("Menu preview")}
              sx={{
                maxWidth: "100%",
                maxHeight: 280,
                borderRadius: "12px",
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("Recognizing dishes from the menu")}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        {dishes && dishes.length > 0 && (
          <Paper variant="outlined" sx={{ borderRadius: "12px", textAlign: "left", mb: 3, overflow: "hidden" }}>
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t("Recognized dishes")}: {dishes.length}
              </Typography>
            </Box>
            <List dense sx={{ maxHeight: 320, overflowY: "auto" }}>
              {dishes.map((dish, index) => (
                <ListItem key={index} divider={index < dishes.length - 1}>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={dish.image || undefined}
                      alt={dish.title}
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" }}
                    >
                      {!dish.image && <RestaurantIcon fontSize="small" />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={dish.title}
                    secondary={dish.description || null}
                  />
                  {dish.price > 0 && (
                    <Typography variant="body2" sx={{ fontWeight: 600, ml: 2, whiteSpace: "nowrap" }}>
                      {dish.price}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {dishes && dishes.length > 0 && (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleCreateAccount}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              borderRadius: "12px",
              boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          >
            {t("Create account with these dishes")}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default UploadMenuSection
